import * as ReferralInfoStyles from './ReferralInfo.styles';

import * as ReferralsStyles from '../../Referrals.styles';
import ReferralsPageIcon from '../../../../assets/icons/ReferralsPageIcon';
import tracking from '../../../../utils/tracking';
import useLinkTracker from '../../../../utils/tracking/useLinkTracker';
import { ExternalLink, PublicRoutePath } from '../../../../utils/routes';
import { TrackingEvent } from '../../../../utils/tracking/types';
import { eligibleToMakeReferrals$, referralBonus$ } from '../../../../store/profile/selectors';
import { getBrandContent } from '../../../../store/features/brandingContent';
import { currencyDefaults, locale } from '../../../../utils/intl';

import { useState } from 'react';
import { useSelector } from 'react-redux';
import copyToClipboard from 'copy-to-clipboard';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

const REFER_LINK_CONDITIONS_COPY = 'Conditions apply';

export interface ReferralInfoProps {
  referralSlug?: string;
  inviteRoute: string;
  shortInviteLinkPath: string;
  isShareSupported: boolean;
}

function ReferralInfo({ inviteRoute, referralSlug, shortInviteLinkPath, isShareSupported }: ReferralInfoProps) {
  const { classes: infoClasses } = ReferralInfoStyles.useReferralInfoStyles();
  const { classes: referralClasses } = ReferralsStyles.useWrapper({});

  const [copied, setCopied] = useState(false);
  const brandingContent = getBrandContent();

  const copyText = copied ? 'Link copied!' : 'Copy link';

  const inviteLinkPath = `${window.location.origin}${inviteRoute}/`;
  const displayInviteRoute = `${window.location.host}${PublicRoutePath.REFER.replace(':slug', '')}`;

  const eligibleToMakeReferrals = useSelector(eligibleToMakeReferrals$);

  const referralBonus = useSelector(referralBonus$);
  const referralBonusText = referralBonus
    ? Intl.NumberFormat(locale, currencyDefaults).format(referralBonus).replace('.00', '')
    : null;

  const termsLinkTrackingRef = useLinkTracker('REFERRAL_TCS');

  const title = `Refer and earn ${referralBonusText || 'concrete cash'}.`;
  const referrals_eligible_copy = `Share this link to refer a tradie. They'll get ${referralBonusText} concrete cash when they sign up. You'll get ${referralBonusText} concrete cash when they complete their first order.`;

  const onClickHandler = async () => {
    tracking.event(TrackingEvent.REFERRAL_SHARE);
    if (isShareSupported) {
      // We place the URL within the text as some apps will ignore the URL field if the text field is populated and vice versa.
      const shareData = {
        title: `Book concrete the easy way with ${brandingContent.fullName}`,
        text: `Register with this link and we’ll both get ${
          referralBonusText || 'concrete cash'
        } to buy concrete after you complete your first order. ${shortInviteLinkPath}`,
      };

      await navigator.share(shareData);
    } else {
      copyToClipboard(inviteLinkPath);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Box className={`${referralClasses.card} ${infoClasses.leftCard}`}>
      <Box
        display="flex"
        flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
        textAlign={{ xs: 'center', sm: 'left' }}
        justifyContent={{ xs: 'center', sm: 'unset' }}
      >
        <Box padding={1}>
          <ReferralsPageIcon />
        </Box>
        <Box paddingLeft={1}>
          <Typography className={referralClasses.mobileH4} variant="h3" component="h3">
            {title}
          </Typography>
          <Typography
            className={`${infoClasses.textLight} ${infoClasses.mobileH6} ${infoClasses.middleText}`}
            variant="h5"
            component="p"
            gutterBottom
          >
            {referrals_eligible_copy}
          </Typography>
          <Link
            variant="h5"
            TypographyClasses={{ root: infoClasses.mobileH6 }}
            ref={termsLinkTrackingRef}
            underline="always"
            href={ExternalLink.REFERRED}
          >
            {REFER_LINK_CONDITIONS_COPY}
          </Link>
        </Box>
      </Box>
      {eligibleToMakeReferrals && (
        <>
          <Box className={infoClasses.linkBox}>
            <Typography className={`${infoClasses.textLight} ${infoClasses.mobileSubtitle}`} variant="h6" component="p">
              {displayInviteRoute}
            </Typography>
            <Typography className={infoClasses.mobileH5} variant="h4" component="p">
              {referralSlug}
            </Typography>
          </Box>
          <Box padding={2}>
            <Button
              className={copied ? infoClasses.buttonCopied : infoClasses.button}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onClickHandler}
              fullWidth
            >
              {isShareSupported ? 'Share link' : copyText}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export default ReferralInfo;
